import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, ProjectVersion, Chapter, StepWithRegions } from '@simultrain/shared';
import { api } from '../api/client.js';

type Level = 'projects' | 'versions' | 'chapters' | 'steps';

type Props = {
  currentProjectId: string;
  currentProjectName: string;
  currentVersionId: string;
  currentChapterId: string;
  currentChapterTitle: string;
  excludeStepId: string;
  onPicked: (assetId: string, sourceVersionId: string) => void;
  onClose: () => void;
};

// Browse Projects ▸ Versions ▸ Chapters ▸ Steps (thumbnails), starting
// already drilled into the chapter currently being edited — reusing a
// screenshot from elsewhere is the common case, browsing further out is the
// exception, so the default view should need zero clicks.
export function ScreenshotPickerModal({
  currentProjectId,
  currentProjectName,
  currentVersionId,
  currentChapterId,
  currentChapterTitle,
  excludeStepId,
  onPicked,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [level, setLevel] = useState<Level>('steps');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState(currentProjectId);
  const [projectName, setProjectName] = useState(currentProjectName);
  const [versions, setVersions] = useState<ProjectVersion[]>([]);
  const [versionId, setVersionId] = useState(currentVersionId);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterTitle, setChapterTitle] = useState(currentChapterTitle);
  const [steps, setSteps] = useState<StepWithRegions[]>([]);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    void loadSteps(currentChapterId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadProjects() {
    setLoading(true);
    setError(null);
    try {
      setProjects(await api.listProjects());
      setLevel('projects');
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadVersions(pid: string, pname: string) {
    setLoading(true);
    setError(null);
    try {
      const proj = await api.getProject(pid);
      setProjectId(pid);
      setProjectName(pname);
      setVersions(proj.versions);
      setLevel('versions');
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadChapters(vid: string) {
    setLoading(true);
    setError(null);
    try {
      setChapters(await api.listChapters(vid));
      setVersionId(vid);
      setLevel('chapters');
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadSteps(cid: string) {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getChapterFull(cid);
      setChapterTitle(data.chapter.title);
      setVersionId(data.chapter.projectVersionId);
      setSteps(data.steps.filter((s) => s.screenshotAssetId && s.id !== excludeStepId));
      setLevel('steps');
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  // Authenticated thumbnails for whatever's currently shown at the "steps" level.
  useEffect(() => {
    if (level !== 'steps') return;
    let active = true;
    const urls: Record<string, string> = {};
    void (async () => {
      const token = JSON.parse(localStorage.getItem('simultrain-studio-auth') ?? '{}')?.state?.accessToken;
      for (const s of steps) {
        if (!s.screenshotAssetId) continue;
        const r = await fetch(api.assetUrl(s.screenshotAssetId), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!r.ok || !active) continue;
        const blob = await r.blob();
        urls[s.id] = URL.createObjectURL(blob);
      }
      if (active) setThumbUrls({ ...urls });
    })();
    return () => {
      active = false;
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, steps]);

  const currentVersionLabel = versions.find((v) => v.id === versionId)?.versionLabel;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--panel)', borderRadius: 10, padding: 20,
        width: '100%', maxWidth: 900, maxHeight: '90vh',
        overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{t('capture.reuseTitle')}</h2>
          <button className="secondary" onClick={onClose}>✕</button>
        </div>

        <div className="row" style={{ fontSize: 13, color: 'var(--muted)', flexWrap: 'wrap', gap: 6 }}>
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => void loadProjects()}>
            {t('projects.title')}
          </span>
          <span>/</span>
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => void loadVersions(projectId, projectName)}
          >
            {projectName}
          </span>
          {(level === 'chapters' || level === 'steps') && (
            <>
              <span>/</span>
              <span
                style={{ cursor: versions.length > 0 ? 'pointer' : 'default', textDecoration: versions.length > 0 ? 'underline' : 'none' }}
                onClick={() => versions.length > 0 && void loadChapters(versionId)}
              >
                v{currentVersionLabel ?? '…'}
              </span>
            </>
          )}
          {level === 'steps' && (
            <>
              <span>/</span>
              <span>{chapterTitle}</span>
            </>
          )}
        </div>

        {error && <div className="error-banner">{error}</div>}
        {loading && <div className="muted">{t('capture.reuseLoading')}</div>}

        {!loading && level === 'projects' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {projects.map((p) => (
              <div key={p.id} className="card" style={{ cursor: 'pointer', marginBottom: 0 }} onClick={() => void loadVersions(p.id, p.name)}>
                {p.name}
              </div>
            ))}
          </div>
        )}

        {!loading && level === 'versions' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {versions.map((v) => (
              <div key={v.id} className="card" style={{ cursor: 'pointer', marginBottom: 0 }} onClick={() => void loadChapters(v.id)}>
                v{v.versionLabel} · {v.status}
              </div>
            ))}
          </div>
        )}

        {!loading && level === 'chapters' && (
          <div style={{ display: 'grid', gap: 8 }}>
            {chapters.length === 0 && <div className="empty">{t('chapters.empty')}</div>}
            {chapters.map((c) => (
              <div key={c.id} className="card" style={{ cursor: 'pointer', marginBottom: 0 }} onClick={() => void loadSteps(c.id)}>
                {c.title}
              </div>
            ))}
          </div>
        )}

        {!loading && level === 'steps' && (
          steps.length === 0 ? (
            <div className="empty">{t('capture.reuseEmpty')}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {steps.map((s) => (
                <div
                  key={s.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => s.screenshotAssetId && onPicked(s.screenshotAssetId, versionId)}
                >
                  <div style={{
                    aspectRatio: '16 / 10', background: '#0b1220', borderRadius: 6,
                    overflow: 'hidden', border: '1px solid var(--border)',
                  }}>
                    {thumbUrls[s.id] && (
                      <img src={thumbUrls[s.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {s.authorLabel || s.instruction.slice(0, 30) || '—'}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
